import React from "react";
import { Badge, Breadcrumb, Card, ErrorAlert, TableRowsPerPage } from "@";
import SearchToggle from "@/shared/components/buttons/SearchToggle";
import { DataTable } from "@/shared/components/tanstackTable";
import { useWhatsappCampaignShowPage } from "../../hooks";
import RecipientsMultiSearch from "../../components/recipients-multi-search";

const Page: React.FC = () => {
  const {
    campaign,
    recipients,
    columns,
    isLoading,
    error,
    statusInfo,
    stats,
    filteredStats,
    pg,
    updatePg,
    refetch,
    search,
    updateSearch,
    handleSearchSubmit,
    handleSearchReset,
  } = useWhatsappCampaignShowPage();

  return (
    <>
      <ErrorAlert reload={refetch} show={error} />
      <Breadcrumb
        links={[
          { name: "حملات WhatsApp", link: "/whatsapp-campaign" },
          { name: campaign?.name || "تفاصيل الحملة", link: "" },
        ]}
      />

      <main className="space-y-5">
        {/* Campaign Info Card */}
        <Card>
          <div className="mb-4 flex items-center justify-start gap-4">
            <h1 className="page-title">
              {isLoading ? "جاري التحميل..." : campaign?.name}
            </h1>
            {statusInfo && (
              <Badge title={statusInfo.title} color={statusInfo.color} />
            )}
          </div>

          {campaign?.description && (
            <p className="mb-4 max-w-4xl text-gray-600 dark:text-gray-400">
              {campaign.description}
            </p>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="rounded-2xl bg-gray-50 p-4 dark:bg-gray-800">
              <p className="text-base text-gray-500 dark:text-gray-400">
                إجمالي المستلمين
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.total}
              </p>
            </div>
            <div className="rounded-2xl bg-green-50 p-4 dark:bg-green-900/20">
              <p className="text-base text-gray-500 dark:text-gray-400">
                تم الإرسال
              </p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.sent}
              </p>
            </div>
            <div className="rounded-2xl bg-red-50 p-4 dark:bg-red-900/20">
              <p className="text-base text-gray-500 dark:text-gray-400">فشل</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {stats.failed}
              </p>
            </div>
            <div className="rounded-2xl bg-yellow-50 p-4 dark:bg-yellow-900/20">
              <p className="text-base text-gray-500 dark:text-gray-400">
                في الانتظار
              </p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {stats.pending}
              </p>
            </div>
          </div>
        </Card>

        {/* Recipients Table */}
        <div className="table-card">
          <header className="flex-container">
            <h2 className="page-title">المستلمين</h2>

            <div className="options-container">
              <TableRowsPerPage
                pagination={{ ...pg, rows: filteredStats.total }}
                rowsPerPageOptions={[5, 10, 15, 25]}
                onRowsPerPageChange={(size: number) =>
                  updatePg({ size, page: 1 })
                }
              />
              <SearchToggle
                toggle={() => updateSearch({ show: !search.show })}
                children={undefined}
              />
            </div>
          </header>

          <RecipientsMultiSearch
            search={search}
            onSubmit={handleSearchSubmit}
            onReset={handleSearchReset}
            updateSearch={updateSearch}
          />

          <DataTable
            data={recipients}
            columns={columns}
            isLoading={isLoading}
            pagination={{ ...pg, rows: filteredStats.total }}
            onPageChange={(page: number) => updatePg({ page })}
            onPageSizeChange={(size: number) => updatePg({ size, page: 1 })}
          />
        </div>
      </main>
    </>
  );
};

export default Page;
